/*
 * Brands (U7) — super-admin brand provisioning.
 *
 * Visible only to a global super-admin (`isSuperAdmin`). Creates a brand and
 * its first admin in one call (`POST /auth/brands`); the admin is provisioned
 * with a password that is immediately usable (no forced change). After success
 * the super-admin gets a "share this password" affordance to hand the new
 * admin their credentials. No roster GET exists server-side, so this is a
 * create-only surface.
 */

import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import {
  Box,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { Alert, Button, Mono, PageHeader } from "../../../components/ui";
import { Section } from "../../../components/config/Section";
import { useAuth } from "../../../auth/AuthContext";
import { errorMessage } from "../../../api/errors";
import { useCreateBrand } from "./hooks";

const DESCRIPTION =
  "Provision brands and their first admin. Each new admin gets a password they can use immediately.";

interface Provisioned {
  brandName: string;
  brandId: string;
  adminEmail: string;
  password: string;
}

export function BrandsPage() {
  const { isSuperAdmin } = useAuth();
  const create = useCreateBrand();

  const [name, setName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [provisioned, setProvisioned] = useState<Provisioned | null>(null);
  const [copied, setCopied] = useState(false);

  // Defense in depth: the route is also nav-gated, but a deep link must not show.
  if (!isSuperAdmin) return <Navigate to="/" replace />;

  function reset() {
    setName("");
    setAdminEmail("");
    setAdminPassword("");
    create.reset();
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !adminEmail.trim() || !adminPassword) return;
    const payload = {
      name: name.trim(),
      admin_email: adminEmail.trim(),
      admin_password: adminPassword,
    };
    create.mutate(payload, {
      onSuccess: (res) => {
        setProvisioned({
          brandName: res.brand.name,
          brandId: res.brand.id,
          adminEmail: res.admin.email,
          password: payload.admin_password,
        });
        setCopied(false);
        reset();
      },
    });
  }

  async function copyPassword() {
    if (!provisioned) return;
    try {
      await navigator.clipboard.writeText(provisioned.password);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <>
      <PageHeader title="Brands" description={DESCRIPTION} />

      {provisioned && (
        <Box maw={520} mb="lg">
          <Paper withBorder radius="md" p="md" bg="var(--mantine-color-default-hover)">
            <Stack gap="sm">
              <Text fw={600} fz="sm">
                Brand “{provisioned.brandName}” created
              </Text>
              <Text fz="sm" c="dimmed">
                Brand id <Mono>{provisioned.brandId}</Mono>. Share this password
                with the admin <strong>{provisioned.adminEmail}</strong>; they
                can use it immediately and change it later. This is the only time
                it is shown.
              </Text>
              <Group gap="sm" wrap="nowrap">
                <TextInput
                  flex={1}
                  readOnly
                  value={provisioned.password}
                  aria-label="Admin password"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button variant="secondary" size="sm" onClick={copyPassword}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              </Group>
              <Group gap="sm">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setProvisioned(null)}
                >
                  Dismiss
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Box>
      )}

      <Box maw={520}>
        <Section
          title="Create a brand"
          description="Provision an isolated workspace and its first admin user."
        >
          <form onSubmit={onSubmit} noValidate>
            <Stack gap="md">
              {create.isError && (
                <Alert tone="error">{errorMessage(create.error)}</Alert>
              )}
              <TextInput
                label="Brand name"
                required
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                placeholder="Acme Performance"
                maxLength={200}
              />
              <TextInput
                label="Admin email"
                required
                type="email"
                autoComplete="off"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.currentTarget.value)}
                placeholder="admin@acme.com"
              />
              <TextInput
                label="Admin password"
                required
                type="text"
                autoComplete="off"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.currentTarget.value)}
                placeholder="A strong initial password"
                minLength={8}
                description="The admin can change this later from their account settings."
              />
              <Group>
                <Button
                  type="submit"
                  variant="primary"
                  loading={create.isPending}
                  disabled={
                    !name.trim() || !adminEmail.trim() || !adminPassword
                  }
                >
                  Create brand
                </Button>
              </Group>
            </Stack>
          </form>
        </Section>
      </Box>
    </>
  );
}

export default BrandsPage;
